import type { RubricBlock } from "../contract/types";
import { cssVars } from "../lib/css";
import { formatPercent } from "../lib/format";
import { TexFormula } from "../components/Math";
import type { BlockViewProps } from "./types";

/**
 * Pesos de evaluación del curso.
 *
 * `RubricComponent.weight` es una **fracción** (0.20, 0.45, 0.35), no un
 * porcentaje: se multiplica por 100 solo al mostrarlo. La barra usa el peso
 * como variable CSS (0→1) y el ancho lo resuelve el CSS con un porcentaje.
 */
export function Rubric({ block }: BlockViewProps<RubricBlock>) {
  const components = block.components ?? [];
  const totalWeight = components.reduce(
    (sum, component) => sum + (Number.isFinite(component.weight) ? component.weight : 0),
    0,
  );

  return (
    <div className="atom atom--rubric rubric">
      {block.total_label ? <p className="rubric__total-label">{block.total_label}</p> : null}

      <ul className="rubric__list">
        {components.map((component) => {
          const weight = Number.isFinite(component.weight) ? component.weight : 0;
          const share = totalWeight > 0 ? weight / totalWeight : 0;
          return (
            <li key={component.code} className="rubric__item">
              <div className="rubric__item-head">
                <span className="rubric__label">{component.label}</span>
                <span className="rubric__weight u-nums">{formatPercent(weight, "fraction")}</span>
              </div>

              <div className="rubric__bar" role="presentation" style={cssVars({ "--weight": share })}>
                <span className="rubric__bar-fill" data-code={component.code} />
              </div>

              {component.description ? (
                <p className="rubric__description">{component.description}</p>
              ) : null}

              {component.modules && component.modules.length > 0 ? (
                <div className="rubric__modules">
                  {component.modules.map((module) => (
                    <span key={module} className="rubric__module">
                      {module}
                    </span>
                  ))}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {block.formula ? (
        <p className="rubric__formula">
          <TexFormula text={block.formula} />
        </p>
      ) : null}
    </div>
  );
}

export default Rubric;
